import { useTranslation } from "@repo/translation";
import { isValidDate, logError } from "@repo/utils";
import { type Locale } from "date-fns";
import { format, formatInTimeZone } from "date-fns-tz";

interface LocalizedDateProps {
  date: string | number | Date;
  timezone?: string;
  showYear?: boolean;
}

export const DATE_FORMAT = "P";

export const LocalizedDate = ({
  date,
  timezone,
  showYear = true,
}: LocalizedDateProps) => {
  const { dateFnsLocale } = useTranslation();
  if (!date) {
    return null;
  }
  return <>{localizeDate(date, dateFnsLocale, timezone, showYear)}</>;
};

export const localizeDate = (
  date: string | number | Date,
  dateFnsLocale?: Locale,
  timezone?: string,
  showYear = true
) => {
  try {
    const d = date instanceof Date ? date : new Date(date);
    if (!isValidDate(d)) {
      return "-";
    }

    if (!showYear) {
      return new Intl.DateTimeFormat(dateFnsLocale?.code, {
        day: "2-digit",
        month: "2-digit",
        ...(timezone ? { timeZone: timezone } : {}),
      }).format(d);
    }

    if (timezone) {
      return formatInTimeZone(d, timezone, DATE_FORMAT, {
        locale: dateFnsLocale,
      });
    }

    return format(d, DATE_FORMAT, {
      locale: dateFnsLocale,
    });
  } catch (e: unknown) {
    logError(e, [{ tagName: "date", tagValue: date?.toString() }]);
    return "-";
  }
};
